<template>
  <div class="board-list-page">
    <!-- Header -->
    <div class="page-header">
      <v-container>
        <h1 class="page-title">กระดานข่าว</h1>
        <p class="page-subtitle">ข่าวสาร บทความ และเรื่องราวที่น่าสนใจจากชุมชน</p>

        <!-- Search -->
        <div class="search-wrapper mt-6">
          <v-text-field
            v-model="search"
            placeholder="ค้นหาบทความ..."
            prepend-inner-icon="mdi-magnify"
            solo
            flat
            hide-details
            clearable
            class="search-input"
            @input="onSearch"
          />
        </div>
      </v-container>
    </div>

    <v-container class="content-container">
      <!-- Loading -->
      <div v-if="isLoading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate size="40" width="3" color="grey darken-1" />
      </div>

      <!-- Empty State -->
      <div v-else-if="items.length === 0" class="empty-state">
        <v-icon size="80" color="grey lighten-1">mdi-file-document-outline</v-icon>
        <h3 class="mt-4">ไม่พบบทความ</h3>
        <p class="grey--text">ลองค้นหาด้วยคำค้นอื่น</p>
      </div>

      <template v-else>
        <!-- Featured Article (First Item) -->
        <div v-if="items.length > 0 && page === 1" class="featured-article" @click="goDetail(items[0].id)">
          <v-row align="center">
            <v-col cols="12" md="7">
              <div class="featured-image-wrapper">
                <v-img :src="toImageUrl(items[0].image)" aspect-ratio="1.6" cover class="featured-image">
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey lighten-3" />
                    </v-row>
                  </template>
                </v-img>
              </div>
            </v-col>
            <v-col cols="12" md="5">
              <div class="featured-content">
                <div class="article-tags mb-3" v-if="items[0].tags && items[0].tags.length">
                  <span v-for="tag in items[0].tags.slice(0, 2)" :key="tag" class="article-tag">
                    {{ tag }}
                  </span>
                </div>
                <h2 class="featured-title">{{ items[0].title }}</h2>
                <p class="featured-excerpt" v-html="getExcerptHtml(items[0].detail, 150)"></p>
                <div class="article-meta">
                  <v-avatar size="32" class="mr-2">
                    <v-icon>mdi-account-circle</v-icon>
                  </v-avatar>
                  <div class="meta-info">
                    <span class="author-name">{{ items[0].author || 'ผู้เขียน' }}</span>
                    <span class="publish-info">
                      {{ formatTimeAgo(items[0].publishedAt) }} · {{ estimateReadTime(items[0].detail) }} นาทีในการอ่าน
                    </span>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-divider v-if="items.length > 1 && page === 1" class="my-8" />

        <!-- Article List -->
        <div class="articles-grid">
          <div
            v-for="(item, index) in displayItems"
            :key="item.id"
            class="article-card"
            @click="goDetail(item.id)"
          >
            <div class="article-card-content">
              <div class="article-card-text">
                <div class="article-tags mb-2" v-if="item.tags && item.tags.length">
                  <span v-for="tag in item.tags.slice(0, 1)" :key="tag" class="article-tag small">
                    {{ tag }}
                  </span>
                </div>
                <h3 class="article-title">{{ item.title }}</h3>
                <p class="article-excerpt" v-html="getExcerptHtml(item.detail, 100)"></p>
                <div class="article-meta small">
                  <span class="author-name">{{ item.author || 'ผู้เขียน' }}</span>
                  <span class="meta-dot">·</span>
                  <span class="publish-date">{{ formatTimeAgo(item.publishedAt) }}</span>
                  <span class="meta-dot">·</span>
                  <span class="read-time">{{ estimateReadTime(item.detail) }} นาที</span>
                </div>
              </div>
              <div class="article-card-image">
                <v-img :src="toImageUrl(item.image)" aspect-ratio="1" cover class="card-image">
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate size="20" color="grey lighten-3" />
                    </v-row>
                  </template>
                </v-img>
              </div>
            </div>
            <v-divider v-if="index < displayItems.length - 1" class="mt-6" />
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination-wrapper">
          <v-pagination
            v-model="page"
            :length="totalPages"
            :total-visible="5"
            color="grey darken-3"
            @input="onPageChange"
          />
        </div>
      </template>
    </v-container>
  </div>
</template>

<script>
import { getBoardsListApi } from "@/services/api/board";

export default {
  name: "BoardList",

  data() {
    return {
      isLoading: false,
      items: [],
      search: "",
      page: 1,
      limit: 10,
      total: 0,
      searchTimeout: null,
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.limit);
    },
    displayItems() {
      // Skip first item on first page (it's featured)
      if (this.page === 1 && this.items.length > 1) {
        return this.items.slice(1);
      }
      return this.items;
    },
  },

  watch: {
    "$route.query": {
      handler() {
        this.initFromQuery();
        this.fetchBoards();
      },
      deep: true,
    },
  },

  async created() {
    this.initFromQuery();
    await this.fetchBoards();
  },

  methods: {
    // Initialize state from URL query params
    initFromQuery() {
      const { page, search } = this.$route.query;
      this.page = parseInt(page) || 1;
      this.search = search || "";
    },

    // Update URL query params
    updateQuery() {
      const query = {};
      if (this.page > 1) {
        query.page = this.page;
      }
      if (this.search) {
        query.search = this.search;
      }

      // Only update if query changed
      const currentQuery = this.$route.query;
      const isSame =
        (currentQuery.page || 1) == (query.page || 1) &&
        (currentQuery.search || "") === (query.search || "");

      if (!isSame) {
        this.$router.push({ query }).catch(() => {});
      }
    },

    async fetchBoards() {
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          limit: this.limit,
        };
        if (this.search) {
          params.search = this.search;
        }
        const res = await getBoardsListApi(params);
        this.items = res.data?.data || [];
        this.total = res.data?.total || 0;
      } catch (e) {
        console.error("getBoardsListApi error:", e);
      } finally {
        this.isLoading = false;
      }
    },

    onSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.updateQuery();
      }, 300);
    },

    onPageChange(page) {
      this.page = page;
      this.updateQuery();
      // Scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    toImageUrl(path) {
      if (!path) return "https://placehold.co/400x300?text=No+Image";
      if (path.startsWith("http")) return path;
      const base = process.env.VUE_APP_API_URL || "";
      return `${base}${path}`;
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      // Strip HTML tags
      const stripped = text.replace(/<[^>]*>/g, "");
      if (stripped.length <= maxLength) return stripped;
      return stripped.substring(0, maxLength) + "...";
    },

    getExcerptHtml(text, maxLength) {
      if (!text) return "";
      
      // Convert markdown to HTML
      let html = text
        // Remove container directives (::: hljs-center :::)
        .replace(/:::\s*[\w-]+\s*/g, "")
        .replace(/:::/g, "")
        // Convert markdown headers to HTML (remove # and make it bold)
        .replace(/^#{1,6}\s+(.+)$/gm, "<strong>$1</strong>")
        // Convert markdown bold
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/__([^_]+)__/g, "<strong>$1</strong>")
        // Convert markdown italic
        .replace(/\*([^*]+)\*/g, "<em>$1</em>")
        .replace(/_([^_]+)_/g, "<em>$1</em>")
        // Convert markdown links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
        // Convert line breaks
        .replace(/\r\n/g, "<br>")
        .replace(/\n/g, "<br>");

      // Strip HTML tags to get plain text length for truncation
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";
      
      if (plainText.length <= maxLength) {
        return html;
      }

      // Truncate plain text
      let truncated = plainText.substring(0, maxLength);
      const lastSpace = truncated.lastIndexOf(" ");
      if (lastSpace > maxLength * 0.7) {
        truncated = truncated.substring(0, lastSpace);
      }
      
      // Simple approach: truncate the original text first, then convert to HTML
      // This ensures we don't break HTML tags
      let truncatedText = text.substring(0, maxLength * 2);
      const lastNewline = truncatedText.lastIndexOf("\n");
      if (lastNewline > maxLength) {
        truncatedText = truncatedText.substring(0, lastNewline);
      }
      
      // Convert truncated markdown to HTML
      let truncatedHtml = truncatedText
        .replace(/:::\s*[\w-]+\s*/g, "")
        .replace(/:::/g, "")
        .replace(/^#{1,6}\s+(.+)$/gm, "<strong>$1</strong>")
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/__([^_]+)__/g, "<strong>$1</strong>")
        .replace(/\*([^*]+)\*/g, "<em>$1</em>")
        .replace(/_([^_]+)_/g, "<em>$1</em>")
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
        .replace(/\r\n/g, "<br>")
        .replace(/\n/g, "<br>");
      
      return truncatedHtml + "...";
    },

    estimateReadTime(text) {
      if (!text) return 1;
      const stripped = text.replace(/<[^>]*>/g, "");
      const words = stripped.split(/\s+/).length;
      const minutes = Math.ceil(words / 200); // 200 words per minute
      return Math.max(1, minutes);
    },

    formatTimeAgo(dateStr) {
      if (!dateStr) return "";
      const published = new Date(dateStr);
      const now = new Date();
      const diffMs = now - published;
      if (diffMs <= 0) return "เมื่อสักครู่";

      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffMinutes < 60) return `${diffMinutes} นาทีที่แล้ว`;

      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours < 24) return `${diffHours} ชั่วโมงที่แล้ว`;

      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays < 7) return `${diffDays} วันที่แล้ว`;

      const weeks = Math.floor(diffDays / 7);
      if (weeks < 4) return `${weeks} สัปดาห์ที่แล้ว`;

      const months = Math.floor(diffDays / 30);
      if (months < 12) return `${months} เดือนที่แล้ว`;

      const years = Math.floor(diffDays / 365);
      return `${years} ปีที่แล้ว`;
    },

    goDetail(id) {
      this.$router.push({ name: "BoardDetail", params: { boardId: id } });
    },
  },
};
</script>

<style scoped>
.board-list-page {
  background: #fff;
  min-height: 100vh;
}

/* Header */
.page-header {
  background: #fafafa;
  padding: 48px 0;
  border-bottom: 1px solid #e6e6e6;
}

.page-title {
  font-size: 42px;
  font-weight: 700;
  color: #242424;
  letter-spacing: -0.5px;
  font-family: 'Georgia', serif;
}

.page-subtitle {
  font-size: 18px;
  color: #6b6b6b;
  margin-top: 8px;
  font-weight: 400;
}

.search-wrapper {
  max-width: 400px;
}

.search-input {
  border-radius: 20px;
  background: #fff !important;
  border: 1px solid #e6e6e6;
}

/* Content */
.content-container {
  max-width: 900px;
  padding-top: 48px;
  padding-bottom: 48px;
}

.empty-state {
  text-align: center;
  padding: 80px 0;
}

.empty-state h3 {
  font-size: 20px;
  color: #242424;
  font-weight: 500;
}

/* Featured Article */
.featured-article {
  cursor: pointer;
  margin-bottom: 16px;
}

.featured-article:hover .featured-title {
  color: #F4811F;
}

.featured-image-wrapper {
  border-radius: 4px;
  overflow: hidden;
}

.featured-content {
  padding: 16px 0;
}

.featured-title {
  font-size: 28px;
  font-weight: 700;
  color: #242424;
  line-height: 1.3;
  margin-bottom: 12px;
  transition: color 0.2s;
  font-family: 'Georgia', serif;
}

.featured-excerpt {
  font-size: 16px;
  color: #6b6b6b;
  line-height: 1.6;
  margin-bottom: 16px;
}

/* Article Tags */
.article-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.article-tag {
  background: #f2f2f2;
  color: #6b6b6b;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.article-tag.small {
  padding: 2px 8px;
  font-size: 12px;
}

/* Article Meta */
.article-meta {
  display: flex;
  align-items: center;
  color: #6b6b6b;
  font-size: 14px;
}

.article-meta.small {
  font-size: 13px;
}

.meta-info {
  display: flex;
  flex-direction: column;
}

.author-name {
  color: #242424;
  font-weight: 500;
}

.publish-info {
  font-size: 13px;
  color: #6b6b6b;
}

.meta-dot {
  margin: 0 6px;
  color: #6b6b6b;
}

.publish-date,
.read-time {
  color: #6b6b6b;
}

/* Article Cards */
.articles-grid {
  display: flex;
  flex-direction: column;
}

.article-card {
  cursor: pointer;
  padding: 24px 0;
}

.article-card:first-child {
  padding-top: 0;
}

.article-card:hover .article-title {
  color: #F4811F;
}

.article-card-content {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.article-card-text {
  flex: 1;
  min-width: 0;
}

.article-title {
  font-size: 20px;
  font-weight: 700;
  color: #242424;
  line-height: 1.4;
  margin-bottom: 8px;
  transition: color 0.2s;
  font-family: 'Georgia', serif;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.article-excerpt {
  font-size: 15px;
  color: #6b6b6b;
  line-height: 1.5;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.article-card-image {
  flex-shrink: 0;
  width: 140px;
  height: 105px;
  border-radius: 4px;
  overflow: hidden;
}

.card-image {
  width: 100%;
  height: 100%;
}

/* Pagination */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid #e6e6e6;
}

/* Responsive */
@media (max-width: 600px) {
  .page-header {
    padding: 32px 0;
  }

  .page-title {
    font-size: 32px;
  }

  .page-subtitle {
    font-size: 16px;
  }

  .featured-title {
    font-size: 22px;
  }

  .article-card-image {
    width: 100px;
    height: 75px;
  }

  .article-title {
    font-size: 17px;
  }

  .article-excerpt {
    display: none;
  }
}
</style>
